/**
 * Running the simulation, and playing it back.
 *
 * Two things are deliberately separated here, and keeping them apart is what makes the
 * playback controls simple:
 *
 *   1. **The run.** Computed once, in the worker, in full. A whole 180-day log is a few
 *      hundred kilobytes and a few milliseconds of work.
 *   2. **The playhead.** An index into that log.
 *
 * Because the entire run already exists before the first frame is drawn, rewinding is
 * just moving an index — no re-simulation, no state to unwind, and scrubbing to day 140
 * and back to day 3 is exact rather than approximate. Comparing two runs is likewise just
 * holding two logs at once.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RunLog, SimulationConfig } from 'walkout-engine';
import type { RunRequest, RunResponse } from '../worker/sim.worker.ts';

export type RunState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'ready'; run: RunLog; elapsedMs: number }
  | { status: 'failed'; error: string };

/** Days advanced per second at each speed setting. */
export const SPEEDS = [1, 2, 4, 10, 30] as const;
export type Speed = (typeof SPEEDS)[number];

function createWorker(): Worker {
  return new Worker(new URL('../worker/sim.worker.ts', import.meta.url), { type: 'module' });
}

/**
 * Runs a config in the worker and returns the log.
 *
 * Requests carry an id and late replies are discarded, so dragging a slider — which fires
 * a run per frame — cannot leave the interface showing the result of a config the user
 * has already moved past.
 */
export function useRun(config: SimulationConfig | null): RunState {
  const [state, setState] = useState<RunState>({ status: 'idle' });
  const workerRef = useRef<Worker | null>(null);
  const latestId = useRef(0);

  useEffect(() => {
    const worker = createWorker();
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent<RunResponse>) => {
      const message = event.data;
      if (message.id !== latestId.current) return; // superseded
      setState(
        message.ok
          ? { status: 'ready', run: message.run, elapsedMs: message.elapsedMs }
          : { status: 'failed', error: message.error },
      );
    };
    worker.onerror = (event) => {
      setState({ status: 'failed', error: event.message || 'The simulation worker failed.' });
    };
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!config || !workerRef.current) return;
    latestId.current += 1;
    const request: RunRequest = { id: latestId.current, config };
    setState({ status: 'running' });
    workerRef.current.postMessage(request);
  }, [config]);

  return state;
}

export interface Playback {
  day: number;
  playing: boolean;
  speed: Speed;
  lastDay: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  step: (delta: number) => void;
  seek: (day: number) => void;
  restart: () => void;
  setSpeed: (speed: Speed) => void;
}

/**
 * The playhead.
 *
 * Driven by `requestAnimationFrame` against elapsed wall-clock time rather than by an
 * interval per simulated day, so playback stays at the requested speed on a slow frame
 * instead of drifting. The clock is a presentation concern and never touches the engine,
 * which has no notion of time beyond its own tick counter.
 */
export function usePlayback(lastDay: number): Playback {
  const [day, setDay] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(4);

  const frame = useRef<number | null>(null);
  const carry = useRef(0);
  const previousStamp = useRef<number | null>(null);

  // A new run arrives: go back to the start rather than leaving the playhead past the end.
  useEffect(() => {
    setDay((current) => Math.min(current, lastDay));
  }, [lastDay]);

  useEffect(() => {
    if (!playing) {
      previousStamp.current = null;
      return;
    }
    const tick = (stamp: number): void => {
      const previous = previousStamp.current;
      previousStamp.current = stamp;
      if (previous !== null) {
        carry.current += ((stamp - previous) / 1000) * speed;
        const whole = Math.floor(carry.current);
        if (whole > 0) {
          carry.current -= whole;
          setDay((current) => {
            const next = current + whole;
            if (next >= lastDay) {
              setPlaying(false);
              return lastDay;
            }
            return next;
          });
        }
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [playing, speed, lastDay]);

  const seek = useCallback(
    (target: number) => {
      carry.current = 0;
      setDay(Math.max(0, Math.min(lastDay, Math.round(target))));
    },
    [lastDay],
  );

  const step = useCallback(
    (delta: number) => {
      setPlaying(false);
      seek(day + delta);
    },
    [day, seek],
  );

  const play = useCallback(() => {
    // Pressing play at the end replays rather than doing nothing.
    setDay((current) => (current >= lastDay ? 0 : current));
    setPlaying(true);
  }, [lastDay]);

  return useMemo(
    () => ({
      day,
      playing,
      speed,
      lastDay,
      play,
      pause: () => setPlaying(false),
      toggle: () => (playing ? setPlaying(false) : play()),
      step,
      seek,
      restart: () => {
        setPlaying(false);
        seek(0);
      },
      setSpeed,
    }),
    [day, playing, speed, lastDay, play, step, seek],
  );
}
